// src/BudgetForm.js
//
// Same dropdown pattern as ActivityForm.js (Project list this time).
// NEW: allocated_amount / spent_amount are DecimalFields on the backend,
// which arrive as JSON STRINGS (e.g. "500.00"). We use type="number"
// inputs and just pass the string values straight through - Django's
// DecimalField parses text like "500.00" from JSON just fine.

import { useState, useEffect } from 'react';
import { API_BASE_URL } from './apiConfig';
import { formatApiError } from './formErrors';

function BudgetForm({ token, budget, onSaved, onCancel }) {
  const [projectOptions, setProjectOptions] = useState([]);

  const [project, setProject] = useState(budget?.project || '');
  const [donor, setDonor] = useState(budget?.donor || '');
  const [allocatedAmount, setAllocatedAmount] = useState(budget?.allocated_amount || '');
  const [spentAmount, setSpentAmount] = useState(budget?.spent_amount || '0');

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/projects/`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setProjectOptions(data);
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      project,
      donor,
      allocated_amount: allocatedAmount,
      spent_amount: spentAmount,
    };

    const url = budget
      ? `${API_BASE_URL}/api/budgets/${budget.id}/`
      : `${API_BASE_URL}/api/budgets/`;
    const method = budget ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        setError(formatApiError(await response.json()));
        setSaving(false);
        return;
      }
      onSaved();
    } catch (err) {
      setError('Could not connect to the server');
      setSaving(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow mb-6 max-w-lg">
      <h2 className="text-lg font-bold mb-4">{budget ? 'Edit Budget' : 'New Budget'}</h2>
      <form onSubmit={handleSubmit}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
        <select value={project} onChange={(e) => setProject(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="" disabled>Select a project...</option>
          {projectOptions.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Donor</label>
        <input type="text" value={donor} onChange={(e) => setDonor(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Allocated Amount</label>
        <input type="number" step="0.01" value={allocatedAmount} onChange={(e) => setAllocatedAmount(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Spent Amount</label>
        <input type="number" step="0.01" value={spentAmount} onChange={(e) => setSpentAmount(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        {error && <p className="text-red-500 text-sm mb-4 break-words">{error}</p>}

        <div className="flex gap-2">
          <button type="submit" disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" onClick={onCancel}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default BudgetForm;
